import { apiRequest } from "@/utils/api";
import { API_ENDPOINTS } from "@/config/api";
import {
  Assessment,
  AssessmentQuestion,
  PaginatedResponse,
  AssessmentTask,
} from "@/types";

export async function getAssessments(
  page = 1,
  limit = 10,
  teamId: string
): Promise<PaginatedResponse<Assessment>> {
  return apiRequest<PaginatedResponse<Assessment>>(
    `${API_ENDPOINTS.assessments.list}?page=${page}&limit=${limit}&team_id=${teamId}`
  );
}

export async function getLatestAssessments(
  count: number,
  teamId: string
): Promise<PaginatedResponse<Assessment>> {
  return apiRequest<PaginatedResponse<Assessment>>(
    `${API_ENDPOINTS.assessments.list}?latest=${count}&team_id=${teamId}`
  );
}

export async function updateAssessmentStatus(
  id: string,
  status: string,
  teamId: string
): Promise<void> {
  await apiRequest(`${API_ENDPOINTS.assessments.list}/${id}`, {
    method: "PATCH",
    body: { status, team_id: teamId },
  });
}

export async function generateAssessment(data: {
  title: string;
  skills: string;
  ideal_candidate: string;
  team_id: string;
}): Promise<{ questions: AssessmentQuestion[] }> {
  return apiRequest(API_ENDPOINTS.assessments.generate, {
    method: "POST",
    body: data,
  });
}

export async function createAssessment(data: {
  title: string;
  language: string;
  location: string;
  importance: string;
  skills: string;
  ideal_candidate: string;
  raw_text: string;
  questions: AssessmentQuestion[];
  team_id: string;
}): Promise<Assessment> {
  return apiRequest(API_ENDPOINTS.assessments.list, {
    method: "POST",
    body: data,
  });
}

export async function sendInvitations(
  assessmentId: string,
  candidates: Array<{ email: string }>,
  teamId: string
): Promise<void> {
  await apiRequest(API_ENDPOINTS.assessments.invite(assessmentId), {
    method: "POST",
    body: { candidates, team_id: teamId },
  });
}

export async function cloneAssessment(id: string, teamId: string): Promise<Assessment> {
  const response = await apiRequest<Assessment>(
    `${API_ENDPOINTS.assessments.list}/${id}/clone`,
    {
      method: "POST",
      body: { team_id: teamId },
    }
  );
  return response;
}

export async function deleteAssessment(id: string, teamId: string): Promise<void> {
  await apiRequest(`${API_ENDPOINTS.assessments.list}/${id}?team_id=${teamId}`, {
    method: "DELETE",
  });
}

export async function getCandidateAssessment(
  candidateId: string,
  email: string,
  start: boolean = false
): Promise<AssessmentTask> {
  return apiRequest(
    `${API_ENDPOINTS.assessments.take(candidateId)}?email=${email}&start=${start}`,
    {
      method: "GET",
    }
  );
}

export async function submitCandidateAssessment(
  candidateId: string,
  email: string,
  answers: Record<string, string>
): Promise<{ success: boolean }> {
  return apiRequest(`${API_ENDPOINTS.assessments.take(candidateId)}/submit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      email,
      answers,
      completed_at: new Date().toISOString(),
    },
  });
}

/**
 * Get candidate assessment results including their responses
 */
export async function getCandidateAssessmentResults(
  candidateId: string,
  teamId: string
): Promise<any> {
  return apiRequest(`${API_ENDPOINTS.assessments.viewResponses(candidateId)}?team_id=${teamId}`, {
    method: "GET",
  });
}
